import React, { useState, useCallback, useEffect, useRef } from 'react';
import { makeStyles } from 'design-system/styles';
import IconToggle from 'design-system/IconToggle';

const blobToBase64 = (blob: Blob) => {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.readAsDataURL(blob);
  });
};

const useStyles = makeStyles({
  audioRow: {
    display: 'flex',
    alignItems: 'center',
  },
  audioPlayer: {
    marginRight: '12px',
    height: '24px',
    flexShrink: 1,
  },
  clearAudio: {
    flexShrink: 0,
  },
});

const AudioAttributeInput = (props: {
  attrKey: string;
  attrValue: string | null | undefined;
  onValueChange: (attrKey: string, mediaBlobUrl: string | null) => void;
}) => {
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const mediaChunks = useRef<Blob[]>([]);
  const mediaStream = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<string>('idle');
  const [error, setError] = useState<string | null>(null);

  const getMediaStream = useCallback(async () => {
    setStatus('acquiring_media');
    const requiredMedia: MediaStreamConstraints = {
      audio: true,
    };

    try {
      const stream = await window.navigator.mediaDevices.getUserMedia(requiredMedia);
      mediaStream.current = stream;

      setStatus('idle');
    } catch (error) {
      setError(error.name);
      setStatus('idle');
    }
  }, []);

  useEffect(() => {
    if (!window.MediaRecorder) {
      throw new Error('Media recording is not supported by browser');
    }

    async function loadStream() {
      await getMediaStream();
    }

    if (!mediaStream.current) {
      loadStream();
    }
  }, [getMediaStream]);

  const startRecording = async () => {
    setError(null);
    if (!mediaStream.current) {
      await getMediaStream();
    }

    if (mediaStream.current) {
      mediaRecorder.current = new MediaRecorder(mediaStream.current);
      mediaRecorder.current.ondataavailable = onRecordingActive;
      mediaRecorder.current.onstop = onRecordingStop;
      mediaRecorder.current.onerror = () => {
        setError('NO_RECORDER');
        setStatus('idle');
      };
      setStatus('recording');
      mediaRecorder.current.start();
    }
  };

  const onRecordingActive = ({ data }: BlobEvent) => {
    mediaChunks.current = [data];
  };

  const stopRecording = () => {
    if (mediaRecorder.current) {
      mediaRecorder.current.stop();
      setStatus('stopping');
    }
  };

  const onRecordingStop = async () => {
    const blobProperty: BlobPropertyBag = { type: 'audio/ogg; codecs=opus' };
    const blob = new Blob(mediaChunks.current, blobProperty);
    const b64 = await blobToBase64(blob);
    setStatus('stopped');
    props.onValueChange(props.attrKey, b64 as string);
  };

  const clearRecording = () => {
    props.onValueChange(props.attrKey, null);
  };

  const classes = useStyles();

  return (
    <div>
      {!props.attrValue && (
        <>
          {(status === 'idle' || status === 'stopped') && (
            <IconToggle
              name="Start Recording"
              isOn={false}
              onClick={startRecording}
              icon="microphone"
              isDisabled={false}
            />
          )}
          {status === 'recording' && (
            <IconToggle
              name="Stop Recording"
              isOn={true}
              onClick={stopRecording}
              icon="microphone"
              isDisabled={false}
            />
          )}
        </>
      )}

      {!!props.attrValue && (
        <div className={classes.audioRow}>
          <audio className={classes.audioPlayer} src={props.attrValue} controls />
          <div className={classes.clearAudio}>
            <IconToggle
              name="Clear Recording"
              isOn={false}
              onClick={clearRecording}
              icon="trash"
              isDisabled={false}
            />
          </div>
        </div>
      )}

      {error}
    </div>
  );
};

export default AudioAttributeInput;
